import React from "react";
import { HOME_STAT_ACCENTS, HOME_THEME as T } from "../../lib/homeTheme";
import "./homeAmbient.css";

export interface HomeStat {
  value: string | number;
  label: string;
}

interface HomeHeroProps {
  greeting: string;
  tagline?: string;
  stats: HomeStat[];
  /** Menos padding vertical para layouts en grid */
  compact?: boolean;
}

function StatPulseDot({ color, index }: { color: string; index: number }) {
  return (
    <span
      className="home-stat-pulse h-2 w-2 shrink-0 rounded-full"
      style={
        {
          backgroundColor: color,
          boxShadow: `0 0 10px ${color}44`,
          "--pulse-dur": `${1.8 + index * 0.35}s`,
          "--pulse-delay": `${index * 0.22}s`,
        } as React.CSSProperties
      }
      aria-hidden
    />
  );
}

export const HomeHero: React.FC<HomeHeroProps> = ({ greeting, tagline, stats, compact = false }) => {
  return (
    <header
      className={`relative text-left ${
        compact ? "pt-16 pb-2 md:pt-20 md:pb-4" : "pt-28 pb-12 md:pt-36 md:pb-20"
      }`}
    >
      <div className="relative z-10">
        <h1
          className="font-light tracking-tight leading-[0.92] text-[clamp(2.75rem,8vw,6.5rem)] max-w-[14ch] whitespace-pre-line bg-clip-text text-transparent text-left"
          style={{ backgroundImage: T.textGradient }}
        >
          {greeting}
        </h1>

        {tagline && (
          <p className="mt-6 max-w-md text-sm leading-relaxed text-left" style={{ color: T.muted }}>
            {tagline}
          </p>
        )}

        {stats.length > 0 && (
          <div
            className={`flex flex-wrap gap-x-8 gap-y-6 text-left ${
              tagline ? "mt-8 md:mt-10" : "mt-10 md:mt-12"
            }`}
          >
            {stats.map((stat, i) => {
              const accent = HOME_STAT_ACCENTS[i % HOME_STAT_ACCENTS.length];
              return (
                <div key={stat.label}>
                  <div className="flex items-center gap-3">
                    <StatPulseDot color={accent} index={i} />
                    <p
                      className="font-extralight tabular-nums leading-none text-[clamp(2rem,5vw,3.5rem)] bg-clip-text text-transparent"
                      style={{ backgroundImage: T.textGradient }}
                    >
                      {stat.value}
                    </p>
                  </div>
                  <p
                    className="mt-2 pl-5 text-[10px] tracking-[0.22em] uppercase"
                    style={{ color: T.muted }}
                  >
                    {stat.label}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </header>
  );
};
